import React, { ReactText, useEffect, useState } from 'react'
import { Input, Table, Alert, Breadcrumb, Card, PageHeader, Button, Col, Row, Space, notification } from 'antd'
import { useCustomerObjectMap } from "../../hooks/useCustomerObjectMap";
import { useHistory } from "react-router-dom";
import { CustomerObjectMapType } from "../../util/providers/CustomerProvider";
import { buildColumns } from './CustomerList/Columns'
import { getUnixTime } from 'date-fns';
import { SearchOutlined } from '@ant-design/icons';
import { FilterDropdownProps } from 'antd/lib/table/interface';
import { parse } from "json2csv";
import ActionsPanel from './CustomerList/ActionsPanel';
import { formatDate } from '../../util';
import { CountryLookup } from '../../util/countries';
import { patch } from "../../config/Axios";

type CustomerReportMadeCSVFriendly = {
  "Email Address": string;
  "Name"?: string;
  "Phone Number"?: string;
  "Country"?: string;
  "Subscription Status"?: string;
  "Plan"?: string;
  "Trial End Date"?: string;
  "Next Billing Date"?: string;
  "Cancel At"?: string;
  "Canceled At"?: string;
};

const createCSV = (customers: CustomerObjectMapType[]) => {
  const preparedData: CustomerReportMadeCSVFriendly[] = [];

  for (const customer of customers) {
    const {
      email,
      familyName,
      givenName,
      businesses,
      phoneNumber,
      "custom:alpha2": alpha2,
    } = customer;

    let customerReport: CustomerReportMadeCSVFriendly = {
      "Email Address": email,
      "Name": givenName && familyName ? `${givenName} ${familyName}` : undefined,
      "Phone Number": phoneNumber,
      "Country": alpha2 ? CountryLookup(alpha2)?.Country : "Unknown",
    };

    const business = businesses ? businesses[0] : null
    const subscription = business?.subscription

    if (subscription) {
      const {
        trial_end,
        status,
        cancel_at,
        canceled_at,
        current_period_end,
        plan: {
          name
        },
      } = subscription;
      const currentPeriodEndFormatted = current_period_end ? formatDate(current_period_end * 1000) : undefined;
      const trialEndFormatted = trial_end ? formatDate(trial_end * 1000) : undefined;
      const cancelAtFormatted = cancel_at ? formatDate(cancel_at * 1000) : undefined;
      const canceledAtFormatted = canceled_at ? formatDate(canceled_at * 1000) : undefined;

      customerReport = {
        ...customerReport,
        "Plan": name,
        "Subscription Status": status,
        "Trial End Date": trialEndFormatted,
        "Next Billing Date": currentPeriodEndFormatted,
        "Cancel At": cancelAtFormatted,
        "Canceled At": canceledAtFormatted,
      }
    }

    preparedData.push(customerReport);
  }

  const fields: (keyof CustomerReportMadeCSVFriendly)[] = [
    "Email Address",
    "Name",
    "Phone Number",
    "Country",
    "Plan",
    "Subscription Status",
    "Trial End Date",
    "Next Billing Date",
    "Cancel At",
    "Canceled At",
  ];

  return new Blob([parse(preparedData, { fields })], {
    type: "text/csv",
  });
};

const CustomerList: React.FC = () => {
  const history = useHistory();
  const { customers, refetch, loading } = useCustomerObjectMap();
  const [orderedCustomers, setOrderedCustomers] = useState<CustomerObjectMapType[] | undefined>();
  const [_searchText, setSearchText] = useState<ReactText | null>(null);
  const [_searchedColumn, setSearchedColumn] = useState<string | null>(null);
  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<Record<string, any>>({});

  const params = new URLSearchParams(history.location.search);

  useEffect(() => {
    if (!customers) return

    const newOrderedCustomers = customers
      .sort((custA, custB) => {
        const businessA = custA.businesses ? custA.businesses[0] : null;
        const businessB = custB.businesses ? custB.businesses[0] : null;
        const subscriptionA = businessA?.subscription;
        const subscriptionB = businessB?.subscription;

        const subscriptionBillingDateA = subscriptionA ? getUnixTime(new Date(subscriptionA.current_period_end * 1000)) : 0;
        const subscriptionBillingDateB = subscriptionB ? getUnixTime(new Date(subscriptionB.current_period_end * 1000)) : 0;

        return subscriptionBillingDateB - subscriptionBillingDateA;
      })

    setOrderedCustomers(newOrderedCustomers);
  }, [customers]);

  const handleTableChange = (_pagination: any, filters: any, sorter: any) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const clearFilters = () => {
    setFilteredInfo({});
    setSortedInfo({});
  }

  const handleSearch = (
    selectedKeys: React.Key[],
    confirm: { (): void },
    dataIndex: string
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: { (): void } | undefined) => {
    if (clearFilters) clearFilters();
    setSearchText("");
  };

  const buildFilterDropdown = (dataIndex: string) => {
    return ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: FilterDropdownProps) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    );
  };

  const handleDownload = () => {
    if (!orderedCustomers) return;

    const csv = createCSV(orderedCustomers)
    const link = document.createElement('a');

    link.href = URL.createObjectURL(csv);
    link.download = `customers-export-${+new Date()}.csv`;
    link.click();
  }

  const handleToggleInvoiced = async ({ code, sub }: { code: string, sub: string }) => {
    if (!customers) return

    try {
      await patch({
        path: `/businesses/${code}/toggleInvoiceFinalized`,
        data: {
          sub,
        }
      });
      const updatedCustomers = customers.map(customer => {
        const business = customer.businesses ? customer.businesses[0] : null
        const subscriptionId = business?.subscription?.id

        if (!customer.businesses) {
          return customer
        }

        if (sub === subscriptionId) {
          if (!business) return customer

          business.invoiceFinalized = !business.invoiceFinalized
          customer.businesses[0] = business
        }

        return customer
      })
      setOrderedCustomers(updatedCustomers)
      notification['success']({
        message: "Success",
        description: "Invoice status toggled",
      });
    } catch (error) {
      notification['error']({
        message: "Error",
        description: "Error toggling invoiced status, please try again",
      });
    }
  }

  const Columns = buildColumns({ history, filters: { buildFilterDropdown, filteredInfo, sortedInfo }, handleToggleInvoiced });

  return (
    <Row gutter={[16, 24]}>
      <Col xs={24}>
        <PageHeader
          className="site-page-header"
          style={{ padding: 0 }}
          title="Customers"
          subTitle="list"
        >
          <Row>
            <Col xs={24}>
              <Card size="small">
                <Breadcrumb>
                  <Breadcrumb.Item>
                    Customers
                  </Breadcrumb.Item>
                </Breadcrumb>
              </Card>
            </Col>
          </Row>
        </PageHeader>
      </Col>

      <Col xs={24}>
        <ActionsPanel
          loading={loading}
          clearFilters={clearFilters}
          download={handleDownload}
          refetch={refetch}
        />
      </Col>

      <Col xs={24}>
        <Table<CustomerObjectMapType>
          rowKey={record => record.sub}
          loading={loading}
          columns={Columns}
          dataSource={orderedCustomers}
          onChange={handleTableChange}
        />
      </Col>
    </Row>
  )
}

export default CustomerList
